import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, ShieldCheck, Wrench, AlertTriangle } from "lucide-react";
import { HOUSE_HOTSPOTS, IMG } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { useLead } from "@/context/LeadContext";

export default function InteractiveHouse() {
  const [active, setActive] = useState(HOUSE_HOTSPOTS[0]);
  const { openLead } = useLead();

  return (
    <section className="bg-navy-deep py-24 md:py-32 relative overflow-hidden grain" data-testid="interactive-house">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-96 bg-royal/20 blur-[120px] rounded-full" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <Reveal>
          <div className="text-center mb-14">
            <span className="text-sky text-xs font-semibold tracking-[0.3em] uppercase">Interactive Experience</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-white mt-3">Explore Your Home's Weak Points</h2>
            <p className="text-slate-300 mt-3 max-w-2xl mx-auto">Tap any glowing point on the structure to see the common problem, our engineering solution, materials and warranty.</p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* House with hotspots */}
          <div className="relative rounded-lg overflow-hidden border border-white/10 aspect-[4/3]">
            <img src={IMG.house} alt="Interactive house waterproofing diagram" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-navy-deep/30" />
            {HOUSE_HOTSPOTS.map((h) => (
              <button
                key={h.id}
                onClick={() => setActive(h)}
                aria-label={h.label}
                data-testid={`hotspot-${h.id}`}
                className="absolute -translate-x-1/2 -translate-y-1/2 group"
                style={{ left: `${h.x}%`, top: `${h.y}%` }}
              >
                <span className={`block h-5 w-5 rounded-full border-2 border-white ${active.id === h.id ? "bg-royal" : "bg-royal/70"} hotspot-pulse`} />
                <span className="absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap glass-dark text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">{h.label}</span>
              </button>
            ))}
          </div>

          {/* Detail panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}
              className="glass-dark rounded-lg p-7 md:p-9"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sky text-xs font-semibold tracking-widest uppercase">{active.label} Waterproofing</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold text-white mb-5">{active.label}</h3>

              <div className="space-y-4">
                <Row icon={AlertTriangle} label="Common Problem" value={active.problem} color="text-amber-400" />
                <Row icon={Wrench} label="Engineering Solution" value={active.solution} color="text-sky" />
                <div className="grid grid-cols-2 gap-4">
                  <Row icon={ShieldCheck} label="Warranty" value={active.warranty} color="text-gold" />
                  <Row icon={Clock} label="Timeline" value={active.timeline} color="text-emerald-400" />
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-7">
                <Link to={`/services/${active.service}`} className="btn-ripple bg-royal hover:bg-royal/90 text-white text-sm font-semibold px-5 py-2.5 rounded-sm flex items-center gap-2 transition-colors" data-testid="house-learn">
                  Learn More <ArrowRight className="h-4 w-4" />
                </Link>
                <button onClick={() => openLead("book_inspection", { prefill: { service: `${active.label} Waterproofing` } })} className="glass text-white text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-white/10 transition-colors" data-testid="house-book">
                  Book Inspection
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

const Row = ({ icon: Icon, label, value, color }) => (
  <div className="flex gap-3">
    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${color}`} />
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-slate-100 text-sm md:text-base">{value}</div>
    </div>
  </div>
);
